// Módulos activables (ARCHITECTURE §8, DOMAIN MOD-*). La web solo muestra u oculta; el servidor decide.
// Delega en @atte/domain (`MODULES`, `isModuleActive`, `features`): las mismas reglas que usa la API.
// Los nombres son los cortos (`shortLabel`), iguales a `MODULE_LABELS` de @atte/contracts (una prueba lo verifica);
// se toman de domain para no cargar los esquemas zod de contracts en el chunk principal.
import type { ModuleKey, ModulesMap, SessionFeatures } from '@atte/contracts';
import { MODULE_KEYS as DOMAIN_MODULE_KEYS, MODULES, features, isModuleActive as domainIsModuleActive } from '@atte/domain';
import { useSession } from './session';

export type { ModuleKey, ModulesMap, SessionFeatures };

export const MODULE_KEYS: readonly ModuleKey[] = DOMAIN_MODULE_KEYS;

function byModule<V>(fn: (key: ModuleKey) => V): Record<ModuleKey, V> {
  return Object.fromEntries(MODULE_KEYS.map((k) => [k, fn(k)])) as Record<ModuleKey, V>;
}

export const MODULE_LABELS: Record<ModuleKey, string> = byModule((k) => MODULES[k].shortLabel);

/** Dependencias (MOD-02). */
export const MODULE_REQUIRES: Record<ModuleKey, readonly ModuleKey[]> = byModule((k) => MODULES[k].requires);

/** MOD-06: atajos derivados de los módulos (igual que `session.features`). */
export function featuresOf(m: ModulesMap): SessionFeatures {
  return features(m);
}

/** Un módulo está "en uso" si está prendido y también lo están los que requiere (MOD-02). */
export function isModuleActive(m: ModulesMap, key: ModuleKey): boolean {
  return domainIsModuleActive(m, key);
}

export interface ModulesState {
  modules: ModulesMap;
  features: SessionFeatures;
  /** Prendido con sus dependencias. */
  isOn: (key: ModuleKey) => boolean;
  label: (key: ModuleKey) => string;
}

/** Módulos del negocio según la sesión (se refresca con SSE `modules.changed`). */
export function useModules(): ModulesState {
  const s = useSession();
  return {
    modules: s.modules,
    features: s.features,
    isOn: (key) => isModuleActive(s.modules, key),
    label: (key) => MODULE_LABELS[key],
  };
}
